import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Box,
  Typography,
  Avatar,
  MenuItem,
  Chip,
  Select,
  FormControl,
  InputLabel,
  Autocomplete,
} from "@mui/material";
import "react-quill/dist/quill.snow.css";
import MDEditor from "@uiw/react-md-editor";
import LoadingPage from "../pages/LoadingPage";
import { useTranslation } from "react-i18next";
import countries from "../countries.json";

const SponsorForm = ({
  open,
  onClose,
  onSubmit,
  uploading,
  successMessage,
  uploadLoading,
  uploadError,
  errorMessage,
  mode,
  sponsor: existingSponsor,
}) => {
  const { t, i18n } = useTranslation();
  const [sponsor, setSponsor] = useState({
    company_name: "",
    email: "",
    country: "",
    website: "",
    stallNo: "",
    sponsorship_level: "",
    profile_pic: null,
    company_profile: "",
  });
  const [preview, setPreview] = useState(
    mode === "edit" ? existingSponsor?.profile_pic || "" : ""
  );
  const [uploadErrorMessage, setUploadErrorMessage] = useState("");
  // console.log("sponsor", sponsor, existingSponsor, mode);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setUploadErrorMessage("Image size should be less than 2MB");
        return;
      }
      setSponsor({ ...sponsor, profile_pic: file });
      setPreview(URL.createObjectURL(file));
      setUploadErrorMessage("");
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (mode === "edit") {
      if (
        !sponsor.company_name ||
        !sponsor.email ||
        !sponsor.country ||
        !sponsor.website ||
        !sponsor.stallNo ||
        !sponsor.company_profile
      ) {
        setUploadErrorMessage("Please fill all required fields");
        return;
      }

      const updateData = {
        id: existingSponsor?.id,
        company_name: sponsor.company_name,
        email: sponsor.email,
        country: sponsor.country,
        website: sponsor.website,
        stallNo: sponsor.stallNo,
        sponsorship_level: sponsor.sponsorship_level,
        company_profile: sponsor.company_profile,
      };

      if (sponsor.profile_pic instanceof File) {
        updateData.profile_pic = sponsor.profile_pic;
      }

      onSubmit(updateData);
    } else {
      if (
        !sponsor.company_name ||
        !sponsor.email ||
        !sponsor.country ||
        !sponsor.website ||
        !sponsor.stallNo ||
        !sponsor.profile_pic ||
        !sponsor.company_profile
      ) {
        setUploadErrorMessage("Please fill all fields");
        return;
      }
      setUploadErrorMessage("");
      onSubmit({
        ...sponsor,
      });
    }
    setSponsor({
      company_name: "",
      email: "",
      country: "",
      website: "",
      stallNo: "",
      sponsorship_level: [],
      profile_pic: null,
      company_profile: "",
    });
    setPreview("");
  };
  useEffect(() => {
    if (mode === "edit" && existingSponsor) {
      setSponsor({
        company_name: existingSponsor.company_name || "",
        email: existingSponsor.email || "",
        country: existingSponsor.country || "",
        website: existingSponsor.website || "",
        stallNo: existingSponsor.stallNo || "",
        sponsorship_level: existingSponsor.sponsorship_level || "",
        company_profile: existingSponsor.company_profile || "",
        profile_pic: null,
      });
      setPreview(existingSponsor.profile_pic || "");
    } else {
      setSponsor({
        company_name: "",
        email: "",
        country: "",
        website: "",
        stallNo: "",
        sponsorship_level: "",
        profile_pic: null,
        company_profile: "",
      });
      setPreview("");
    }
  }, [mode, existingSponsor]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      dir={i18n.dir()}
    >
      <DialogTitle sx={{ bgcolor: "primary.main", color: "white" }}>
        {mode === "edit" ? t("Editsponsor") : t("AddNewsponsor")}
      </DialogTitle>
      {uploadLoading ? (
        <LoadingPage />
      ) : (
        <DialogContent sx={{ pt: 3 }}>
          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label={t("CompanyName")}
              value={sponsor.company_name}
              onChange={(e) =>
                setSponsor({ ...sponsor, company_name: e.target.value })
              }
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label={t("Email")}
              type="email"
              value={sponsor.email}
              onChange={(e) =>
                setSponsor({ ...sponsor, email: e.target.value })
              }
              margin="normal"
              required
            />
            <Autocomplete
              options={countries}
              getOptionLabel={(option) => option.label}
              value={
                countries.find(
                  (country) => country.label === sponsor.country
                ) || null
              }
              onChange={(event, value) => {
                setSponsor((prev) => ({
                  ...prev,
                  country: value?.label || "",
                }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("Country")}
                  variant="outlined"
                  margin="normal"
                  required
                />
              )}
            />
            <TextField
              fullWidth
              label={t("Website")}
              type="url"
              value={sponsor.website}
              onChange={(e) =>
                setSponsor({ ...sponsor, website: e.target.value })
              }
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label={t("StallNo")}
              value={sponsor.stallNo}
              onChange={(e) =>
                setSponsor({ ...sponsor, stallNo: e.target.value })
              }
              margin="normal"
              required
            />

            <FormControl fullWidth margin="normal">
              <InputLabel>{t("sponsorship_level")}</InputLabel>
              <Select
                value={sponsor.sponsorship_level}
                onChange={(e) =>
                  setSponsor({ ...sponsor, sponsorship_level: e.target.value })
                }
                label={t("sponsorship_level")}
              >
                {[
                  "Hosted By",
                  "Strategic Partners",
                  "Smart Cities Partner",
                  "Platinum Sponsor",
                  "Gold Sponsor",
                  "Lanyard Sponsor",
                  "Supporting Partners",
                ].map((level) => (
                  <MenuItem key={level} value={level}>
                    {level}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ my: 2 }}>
              <input
                accept="image/*"
                style={{ display: "none" }}
                id="logo-upload"
                type="file"
                onChange={handleImageChange}
              />
              <label htmlFor="logo-upload">
                <Button variant="outlined" component="span">
                  {t("UploadLogo")}
                </Button>
              </label>
              <br />
              <span>{t("ForBestViewUseDimension500x300")}</span>
              {preview && (
                <Avatar
                  src={preview}
                  variant="rounded"
                  sx={{
                    width: 250,
                    height: 150,
                    mt: 2,
                    border: "2px dashed",
                    borderColor: "primary.main",
                    objectFit: "contain",
                  }}
                />
              )}
            </Box>
            <Box sx={{ my: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t("CompanyProfile")}
              </Typography>
              <MDEditor
                value={sponsor.company_profile}
                onChange={(value) =>
                  setSponsor({ ...sponsor, company_profile: value })
                }
                preview="edit"
                height={200}
                visibleDragbar={false}
              />
            </Box>
            <Box sx={{ my: 2 }}>
              {successMessage && (
                <Typography color="success.main" sx={{ mt: 2 }}>
                  {successMessage}
                </Typography>
              )}
              {(errorMessage || uploadError || uploadErrorMessage) && (
                <Typography color="error.main" sx={{ mt: 2 }}>
                  {errorMessage}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
      )}
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ mx: 1 }} color="secondary">
          {t("Cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={uploading}
          sx={{ mx: 1 }}
        >
          {uploading ? (
            <CircularProgress size={24} color="inherit" />
          ) : mode === "edit" ? (
            t("Updatesponsor")
          ) : (
            t("Addsponsor")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SponsorForm;
